/**
 * Flow Compiler for Flow2
 *
 * This is the core compiler that takes Content + Layout + Style and produces
 * a valid FlowVella Flow JSON structure.
 *
 * @packageDocumentation
 * @module ai/compiler
 */

import Flow from '../../model/Flow';
import Screens from '../../model/Screens';
import Color from '../../model/Color';
import TextFlowObject from '../../model/TextFlowObject';
import ShapeFlowObject from '../../model/ShapeFlowObject';
import ImageAsset from '../../model/Assets';
import FontAttrsArray, { Alignment } from '../../model/FontAttrsArray';
import { guidGenerator, getCurrentDate } from '../../features/utils/commonFunctions';
import { ScreenLinkAction } from '../../model/Action';

import {
  PresentationContent,
  ScreenContent,
  ContentBlock,
  ContentBlockType,
  isTextBlock,
  isListBlock,
  isMediaPlaceholder,
  TitleBlock,
  SubtitleBlock,
  BodyBlock,
  BulletListBlock,
  NumberedListBlock,
  QuoteBlock,
  CalloutBlock,
  ImagePlaceholderBlock,
  SpeakerNotesBlock,
  TOCListBlock,
  TOCEntry,
  TimelineBlock,
  TimelineEntry,
  StatBlock,
  ChartBlock,
} from '../schemas/ContentBlock';

import {
  LayoutTemplate,
  LayoutSlot,
  SlotAssignment,
  LayoutCompilationResult,
  calculateLayoutScore,
  frameToPixels,
  DEFAULT_VIEW_SIZE,
} from '../schemas/LayoutTemplate';

import {
  StyleTheme,
  getBlockStyle,
  colorToIOSFormat,
  hexToRgb,
} from '../schemas/StyleTheme';

import { PREDEFINED_LAYOUTS, DEFAULT_LAYOUT } from '../layouts/predefinedLayouts';
import { DEFAULT_THEME } from '../styles/themeLibrary';
import {
  DecorationElement,
  getDecorationsForScreen,
  getDecorationsForScreenWithConform,
  parseDecorationColor,
  GradientDef,
} from '../styles/themeDecorations';
import ImageFlowObject from '../../model/ImageFlowObject';
import {
  ScreenCompilationContext,
  CompilationRule,
  runCompilationRules,
  DEFAULT_COMPILATION_RULES,
} from './CompilationRules';

/**
 * Compilation context containing all inputs
 */
export interface CompilationContext {
  presentation: PresentationContent;
  layouts: Map<string, LayoutTemplate>;
  theme: StyleTheme;
  viewSize: { width: number; height: number };
  /** Per-screen layout overrides (from template preferredLayoutId) */
  overrideLayoutIds?: string[];
  /** Optional custom compilation rules (defaults to DEFAULT_COMPILATION_RULES) */
  compilationRules?: CompilationRule[];
}

/**
 * Mapping from content block ID to generated MMO IDs
 */
export interface ContentBlockMapping {
  blockId: string;
  mmoIds: string[];
  screenIndex: number;
  customPosition?: boolean;
}

/**
 * Complete content mapping for a flow
 */
export interface ContentMapping {
  [blockId: string]: {
    screenIndex: number;
    mmoIds: string[];
    customPosition?: boolean;
  };
}

/**
 * Result of full flow compilation
 */
export interface FlowCompilationResult {
  flow: Flow;
  warnings: string[];
  screenResults: LayoutCompilationResult[];
  contentMapping: ContentMapping;
  layoutIds: string[];
}

/**
 * Main Flow Compiler class
 */
export class FlowCompiler {
  private context: CompilationContext;
  private warnings: string[] = [];
  /** Pre-generated screen IDs for TOC navigation links */
  private screenIdMap: Map<number, string> = new Map();

  constructor(context: Partial<CompilationContext> = {}) {
    this.context = {
      presentation: context.presentation || this.createEmptyPresentation(),
      layouts: context.layouts || PREDEFINED_LAYOUTS,
      theme: context.theme || DEFAULT_THEME,
      viewSize: context.viewSize || DEFAULT_VIEW_SIZE,
    };
  }

  /**
   * Main compilation method
   */
  compile(presentation?: PresentationContent): FlowCompilationResult {
    if (presentation) {
      this.context.presentation = presentation;
    }

    this.warnings = [];
    const screenResults: LayoutCompilationResult[] = [];
    const contentMapping: ContentMapping = {};
    const layoutIds: string[] = [];

    // Pre-generate screen IDs for all screens (needed for TOC navigation links)
    this.screenIdMap = new Map();
    this.context.presentation.screens.forEach((screen, index) => {
      this.screenIdMap.set(index, screen.id || guidGenerator());
    });

    // Compile each screen (with optional per-screen layout overrides)
    const overrides = this.context.overrideLayoutIds;
    const compiledScreens = this.context.presentation.screens.map((screenContent, index) => {
      const overrideLayoutId = overrides && overrides[index] ? overrides[index] : undefined;
      const result = this.compileScreen(screenContent, index, overrideLayoutId);
      screenResults.push(result);
      layoutIds.push(result.layoutId);

      // Aggregate content mapping from this screen
      if (result.blockMappings) {
        for (const mapping of result.blockMappings) {
          contentMapping[mapping.blockId] = {
            screenIndex: index,
            mmoIds: mapping.mmoIds,
            customPosition: false,
          };
        }
      }

      return result;
    });

    // Create the Flow object
    const flow = this.createFlow(compiledScreens);

    return {
      flow,
      warnings: this.warnings,
      screenResults,
      contentMapping,
      layoutIds,
    };
  }

  /**
   * Compile a single screen
   * @param overrideLayoutId - If provided, use this layout instead of scoring algorithm
   */
  private compileScreen(
    screenContent: ScreenContent,
    screenIndex: number,
    overrideLayoutId?: string
  ): LayoutCompilationResult & { screen: Screens } {
    // 1. Select layout: use override if provided, otherwise score-based selection
    const layout = overrideLayoutId
      ? this.context.layouts.get(overrideLayoutId) || this.selectLayout(screenContent)
      : this.selectLayout(screenContent);

    // 2. Assign content blocks to layout slots
    const { assignments, unassignedBlocks, unusedSlots } = this.assignBlocksToSlots(
      screenContent.blocks,
      layout
    );

    // 3. Generate decoration MMOs (z-bottom, behind content)
    const decorationMMOs = this.generateDecorationMMOs(screenContent.screenType);

    // 4. Generate content MMOs from assignments (with block-to-MMO mapping)
    const { mmos, blockMappings } = this.generateMMOs(screenContent.blocks, assignments, layout);

    // 4.5 Run compilation rules (contrast, vertical centering, callout bg, overflow)
    const blockMap = new Map(screenContent.blocks.map((b) => [b.id, b]));
    const blockForMMO = new Map<string, ContentBlock>();
    const slotForMMO = new Map<string, LayoutSlot>();
    for (const mapping of blockMappings) {
      const block = blockMap.get(mapping.blockId);
      const assignment = assignments.find((a) => a.blockId === mapping.blockId);
      const slot = assignment ? layout.slots.find((s) => s.id === assignment.slotId) : undefined;
      for (const mmoId of mapping.mmoIds) {
        if (block) blockForMMO.set(mmoId, block);
        if (slot) slotForMMO.set(mmoId, slot);
      }
    }

    const ruleContext: ScreenCompilationContext = {
      screenIndex,
      screenContent,
      screenBackgroundColor: this.getBackgroundColor(screenContent.backgroundHint),
      decorationMMOs,
      contentMMOs: mmos,
      blockForMMO,
      slotForMMO,
      theme: this.context.theme,
      layout,
      viewSize: this.context.viewSize,
      warnings: [],
    };

    runCompilationRules(ruleContext, this.context.compilationRules || DEFAULT_COMPILATION_RULES);
    this.warnings.push(...ruleContext.warnings);

    // 5. Create screen object — decorations first (behind), then content on top
    const allMMOs = [...decorationMMOs, ...mmos];
    const screen = this.createScreen(screenContent, allMMOs, screenIndex);

    // 6. Handle speaker notes
    const speakerNotes = screenContent.blocks.find(
      (b): b is SpeakerNotesBlock => b.type === 'speaker-notes'
    );
    if (speakerNotes) {
      screen.presenterNotes = speakerNotes.text;
    }

    // Generate warnings
    const localWarnings: string[] = [];
    if (unassignedBlocks.length > 0) {
      localWarnings.push(
        `Screen ${screenIndex + 1}: ${unassignedBlocks.length} content block(s) could not be placed`
      );
    }
    if (unusedSlots.length > 0) {
      localWarnings.push(
        `Screen ${screenIndex + 1}: ${unusedSlots.length} layout slot(s) unused`
      );
    }
    this.warnings.push(...localWarnings);

    return {
      layoutId: layout.id,
      assignments,
      unassignedBlocks,
      unusedSlots,
      warnings: localWarnings,
      screen,
      blockMappings,
    };
  }

  /**
   * Select the best layout for a screen's content.
   * Factors in theme layout preferences as a score boost.
   */
  private selectLayout(screenContent: ScreenContent): LayoutTemplate {
    const blockTypes = screenContent.blocks
      .filter((b) => b.type !== 'speaker-notes')
      .map((b) => b.type);
    const prefs = this.context.theme.layoutPreferences;
    let bestLayout: LayoutTemplate = DEFAULT_LAYOUT;
    let bestScore = -Infinity;

    for (const layout of this.context.layouts.values()) {
      let score = calculateLayoutScore(layout, blockTypes, screenContent.screenType);
      if (score < 0) continue; // Layout is incompatible with content

      // Theme layout preference boost (thin coupling: Style → Layout)
      if (prefs) {
        // Strong preference: per-screenType layout override
        if (prefs.screenTypeLayouts?.[screenContent.screenType] === layout.id) {
          score += 3;
        }
        // Moderate preference: generally preferred layouts for this theme
        if (prefs.preferredLayoutIds?.includes(layout.id)) {
          score += 1.5;
        }
      }

      // Image-aware scoring: prefer layouts whose image capacity matches the content
      const imageBlockCount = blockTypes.filter(t => t === 'image-placeholder').length;
      if (imageBlockCount > 0) {
        const imageSlotCount = layout.slots.filter(s => s.acceptedTypes.includes('image-placeholder')).length;
        if (imageSlotCount > 0) {
          // Base boost for having any image slots
          score += 2;
          // Bonus when image slot count closely matches image block count
          const match = Math.min(imageBlockCount, imageSlotCount) / Math.max(imageBlockCount, imageSlotCount);
          score += match * 2;
        }
      }

      // Content coverage penalty — dry-run assignment to detect dropped blocks
      const dryRun = this.assignBlocksToSlots(
        screenContent.blocks.filter(b => b.type !== 'speaker-notes'),
        layout
      );
      const unassignedTextBlocks = dryRun.unassignedBlocks.filter(blockId => {
        const block = screenContent.blocks.find(b => b.id === blockId);
        return block && block.type !== 'image-placeholder' && block.type !== 'speaker-notes';
      });
      if (unassignedTextBlocks.length > 0) {
        score -= unassignedTextBlocks.length * 3;
      }

      if (score > bestScore) {
        bestScore = score;
        bestLayout = layout;
      }
    }

    return bestLayout;
  }

  /**
   * Assign content blocks to layout slots using a greedy algorithm
   */
  private assignBlocksToSlots(
    blocks: ContentBlock[],
    layout: LayoutTemplate
  ): { assignments: SlotAssignment[]; unassignedBlocks: string[]; unusedSlots: string[] } {
    const assignments: SlotAssignment[] = [];
    const assignedBlockIds = new Set<string>();
    const usedSlotIds = new Set<string>();

    // Sort slots by priority (higher first)
    const sortedSlots = [...layout.slots].sort(
      (a, b) => (b.priority || 0) - (a.priority || 0)
    );

    // Sort blocks by priority: title > subtitle > body > lists > others
    const blockPriority: Record<ContentBlockType, number> = {
      'title': 100,
      'subtitle': 90,
      'body': 80,
      'bullet-list': 70,
      'numbered-list': 70,
      'quote': 60,
      'stat': 55, // Stats are important KPIs, render above images
      'chart': 52, // Charts are data visualizations, render above images
      'callout': 50,
      'toc-list': 45, // TOC list rendered as navigation buttons
      'timeline': 44, // Timeline rendered as line + event cards
      'image-placeholder': 40,
      'video-placeholder': 40,
      'chart-data': 30,
      'table-data': 30,
      'code-block': 20,
      'caption': 10,
      'speaker-notes': 0, // Handle separately
    };

    const sortedBlocks = [...blocks]
      .filter((b) => b.type !== 'speaker-notes')
      .sort((a, b) => (blockPriority[b.type] || 0) - (blockPriority[a.type] || 0));

    // Greedy assignment
    for (const slot of sortedSlots) {
      // Skip notes slots
      if (slot.acceptedTypes.includes('speaker-notes')) continue;

      // Find best matching block for this slot
      let bestBlock: ContentBlock | null = null;
      let bestScore = -1;

      for (const block of sortedBlocks) {
        if (assignedBlockIds.has(block.id)) continue;
        if (!slot.acceptedTypes.includes(block.type)) continue;

        // Calculate match score
        let score = 1;

        // Bonus for exact type match
        if (slot.acceptedTypes.length === 1) {
          score += 2;
        }

        // Bonus for required slots
        if (slot.required) {
          score += 1;
        }

        if (score > bestScore) {
          bestScore = score;
          bestBlock = block;
        }
      }

      if (bestBlock) {
        assignments.push({
          slotId: slot.id,
          blockId: bestBlock.id,
          blockType: bestBlock.type,
        });
        assignedBlockIds.add(bestBlock.id);
        usedSlotIds.add(slot.id);
      }
    }

    // Collect unassigned blocks and unused slots
    const unassignedBlocks = sortedBlocks
      .filter((b) => !assignedBlockIds.has(b.id))
      .map((b) => b.id);

    const unusedSlots = sortedSlots
      .filter((s) => !usedSlotIds.has(s.id) && !s.acceptedTypes.includes('speaker-notes'))
      .map((s) => s.id);

    return { assignments, unassignedBlocks, unusedSlots };
  }

  /**
   * Generate MMO objects from assignments
   * Returns both the MMOs and a mapping of which block produced which MMO
   */
  private generateMMOs(
    blocks: ContentBlock[],
    assignments: SlotAssignment[],
    layout: LayoutTemplate
  ): {
    mmos: (TextFlowObject | ShapeFlowObject | ImageFlowObject)[];
    blockMappings: { blockId: string; mmoIds: string[] }[];
  } {
    const mmos: (TextFlowObject | ShapeFlowObject | ImageFlowObject)[] = [];
    const blockMappings: { blockId: string; mmoIds: string[] }[] = [];
    const blockMap = new Map(blocks.map((b) => [b.id, b]));

    for (const assignment of assignments) {
      const block = blockMap.get(assignment.blockId);
      const slot = layout.slots.find((s) => s.id === assignment.slotId);

      if (!block || !slot) continue;

      // Handle toc-list blocks specially
      if (block.type === 'toc-list') {
        const tocResult = this.tocListToMMOs(block as TOCListBlock, slot);
        mmos.push(...tocResult.mmos);
        blockMappings.push({
          blockId: block.id,
          mmoIds: tocResult.mmos.map((m) => m.id),
        });
        continue;
      }

      // Handle timeline blocks specially
      if (block.type === 'timeline') {
        const timelineResult = this.timelineToMMOs(block as TimelineBlock, slot);
        mmos.push(...timelineResult.mmos);
        blockMappings.push({
          blockId: block.id,
          mmoIds: timelineResult.mmos.map((m) => m.id),
        });
        continue;
      }

      // Handle stat blocks
      if (block.type === 'stat') {
        const statResult = this.statToMMOs(block as StatBlock, slot);
        mmos.push(...statResult.mmos);
        blockMappings.push({
          blockId: block.id,
          mmoIds: statResult.mmos.map((m) => m.id),
        });
        continue;
      }

      // Handle chart blocks
      if (block.type === 'chart') {
        const chartResult = this.chartToMMOs(block as ChartBlock, slot);
        mmos.push(...chartResult.mmos);
        blockMappings.push({
          blockId: block.id,
          mmoIds: chartResult.mmos.map((m) => m.id),
        });
        continue;
      }

      const mmo = this.blockToMMO(block, slot);
      if (mmo) {
        mmos.push(mmo);
        // Track the mapping from this block to the generated MMO
        blockMappings.push({
          blockId: block.id,
          mmoIds: [mmo.id],
        });
      }
    }

    return { mmos, blockMappings };
  }

  /**
   * Convert a content block to a TextFlowObject
   */
  private blockToMMO(
    block: ContentBlock,
    slot: LayoutSlot
  ): TextFlowObject | ShapeFlowObject | ImageFlowObject | null {
    const frame = frameToPixels(slot.frame, this.context.viewSize);
    const style = getBlockStyle(this.context.theme, block.type);

    // Merge slot default style with theme style
    const mergedStyle = { ...style, ...slot.defaultStyle };

    // Get text content based on block type
    let text = '';
    if (isTextBlock(block)) {
      if (block.type === 'quote') {
        text = `"${block.text}"`;
        if ((block as QuoteBlock).attribution) {
          text += `\n\n- ${(block as QuoteBlock).attribution}`;
        }
      } else {
        text = block.text;
      }
    } else if (isListBlock(block)) {
      const listBlock = block as BulletListBlock | NumberedListBlock;
      const prefix = block.type === 'bullet-list' ? '•' : '';
      text = listBlock.items
        .map((item, i) => {
          if (block.type === 'numbered-list') {
            const start = (block as NumberedListBlock).startNumber || 1;
            return `${start + i}. ${item}`;
          }
          return `${prefix} ${item}`;
        })
        .join('\n');
    } else if (isMediaPlaceholder(block)) {
      const imgBlock = block as ImagePlaceholderBlock;
      if (imgBlock.resolvedImageUrl) {
        // Full-bleed by default; inline only for book/logo/person/product searchTypes
        const inlineTypes = ['book', 'logo', 'person', 'product'];
        const forceInline = inlineTypes.includes(imgBlock.searchType || '');
        const useFullBleed = !forceInline;
        return this.createImageMMO(imgBlock, frame, useFullBleed);
      }
      return this.createPlaceholderShape(imgBlock, frame);
    }

    if (!text) return null;

    // Create TextFlowObject
    return this.createTextFlowObject(text, frame, mergedStyle);
  }

  /**
   * Convert a TOC list block to multiple MMOs (buttons with navigation actions).
   * Each TOC entry becomes a ShapeFlowObject (button bg) + TextFlowObject (icon + title),
   * with a ScreenLinkAction attached for navigation.
   */
  private tocListToMMOs(
    block: TOCListBlock,
    slot: LayoutSlot
  ): { mmos: (TextFlowObject | ShapeFlowObject)[] } {
    const mmos: (TextFlowObject | ShapeFlowObject)[] = [];
    const entries = block.entries;
    const frame = frameToPixels(slot.frame, this.context.viewSize);

    if (entries.length === 0) {
      return { mmos };
    }

    // Layout settings
    const BUTTON_HEIGHT = 56;
    const BUTTON_GAP = 12;
    const BUTTON_RADIUS = 12;
    const PADDING_H = 16;
    const FONT_SIZE = 24;

    // Determine grid vs list layout
    const useGrid = entries.length >= 6;
    const columns = useGrid ? (entries.length >= 9 ? 3 : 2) : 1;
    const buttonWidth = useGrid
      ? (frame.width - BUTTON_GAP * (columns - 1)) / columns
      : frame.width;

    // Theme colors for button styling
    const accentHex = this.context.theme.colors.accent?.[0] ||
      this.context.theme.colors.primary ||
      '#3B82F6';
    const accentRgb = hexToRgb(accentHex);
    const buttonBgColor = accentRgb
      ? new Color(accentRgb.r, accentRgb.g, accentRgb.b, 0.1)
      : new Color(59, 130, 246, 0.1);

    // Text style from theme
    const bodyStyle = getBlockStyle(this.context.theme, 'body');
    const textColorHex = bodyStyle.textColor || '#000000';
    const textColor = this.parseColor(textColorHex);
    const fontName = bodyStyle.fontName || 'HelveticaNeue-Medium';

    entries.forEach((entry, index) => {
      // Calculate position (grid or list)
      const col = useGrid ? index % columns : 0;
      const row = useGrid ? Math.floor(index / columns) : index;

      const buttonX = frame.x + col * (buttonWidth + BUTTON_GAP);
      const buttonY = frame.y + row * (BUTTON_HEIGHT + BUTTON_GAP);

      // Get target screen ID from pre-generated map
      const targetScreenId = this.screenIdMap.get(entry.screenIndex) || '';

      // Create button background shape
      const [shapeType, pointsArrayString] = ShapeFlowObject.derivePointsArray('rectangle');
      const buttonShape = new ShapeFlowObject(
        guidGenerator(),
        Color.iosToCssColor('00000000'), // transparent border
        0, // rotation
        null,
        getCurrentDate(),
        {
          x: buttonX,
          y: buttonY,
          width: buttonWidth,
          height: BUTTON_HEIGHT,
        },
        null,
        1, // alpha
        null,
        null,
        'shape',
        null,
        null,
        0,
        { height: 0, width: 0 },
        null,
        null,
        getCurrentDate(),
        false,
        buttonBgColor,
        shapeType,
        pointsArrayString
      );

      // Set corner radius for rounded button
      buttonShape.cornerRadius = BUTTON_RADIUS;

      // Attach screen link action (set directly, not wrapped in __tag for PHP compatibility)
      if (targetScreenId) {
        (buttonShape as any).action = new ScreenLinkAction('2', targetScreenId); // '2' = Fade transition
      }

      mmos.push(buttonShape);

      // Create text label with emoji icon + title
      const labelText = `${entry.icon}  ${entry.title}`;
      const textLength = labelText.length;
      const fontArrayString = `Alignment:${Alignment.LEFT}|Color:${textColor.asIOS()}|Name:${fontName}|Size:${FONT_SIZE.toFixed(6)}|Range:{0, ${textLength}}`;
      const fontArrayStringSimple = `Name:${fontName}|Size:${FONT_SIZE.toFixed(6)}|Range:{0, ${textLength}}`;

      const textLabel = new TextFlowObject(
        guidGenerator(),
        Color.default(),
        0,
        null,
        getCurrentDate(),
        {
          x: buttonX + PADDING_H,
          y: buttonY + (BUTTON_HEIGHT - FONT_SIZE) / 2 - 4, // vertically center
          width: buttonWidth - PADDING_H * 2,
          height: FONT_SIZE + 8,
        },
        null,
        1,
        null,
        null,
        'text',
        null,
        null,
        0,
        { height: 0, width: 0 },
        null,
        null,
        getCurrentDate(),
        false,
        false,
        textColor,
        [FontAttrsArray.fromString(fontArrayStringSimple)],
        null,
        Alignment.LEFT,
        {
          fontSize: FONT_SIZE,
          fontName,
        },
        null,
        labelText,
        [FontAttrsArray.fromString(fontArrayString)]
      );

      // Attach same screen link action to text (set directly for PHP compatibility)
      if (targetScreenId) {
        (textLabel as any).action = new ScreenLinkAction('2', targetScreenId);
      }

      mmos.push(textLabel);
    });

    return { mmos };
  }

  /**
   * Convert a timeline block to MMOs (horizontal line + alternating event cards).
   * Creates a horizontal line with markers and event cards above/below.
   */
  private timelineToMMOs(
    block: TimelineBlock,
    slot: LayoutSlot
  ): { mmos: (TextFlowObject | ShapeFlowObject)[] } {
    const mmos: (TextFlowObject | ShapeFlowObject)[] = [];
    const entries = block.entries;
    const frame = frameToPixels(slot.frame, this.context.viewSize);

    if (entries.length === 0) {
      return { mmos };
    }

    // Layout constants
    const LINE_HEIGHT = 4;
    const LINE_Y = frame.y + frame.height / 2; // Center line vertically
    const MARKER_SIZE = 16;
    const CARD_HEIGHT = 80;
    const CARD_WIDTH = 140;
    const CARD_GAP = 20;
    const LABEL_HEIGHT = 20;
    const TITLE_FONT_SIZE = 14;
    const LABEL_FONT_SIZE = 11;
    const DESC_FONT_SIZE = 11;

    // Theme colors
    const accentHex = this.context.theme.colors.accent?.[0] ||
      this.context.theme.colors.primary ||
      '#3B82F6';
    const accentRgb = hexToRgb(accentHex);
    const lineColor = accentRgb
      ? new Color(accentRgb.r, accentRgb.g, accentRgb.b, 1)
      : new Color(59, 130, 246, 1);
    const markerColor = lineColor;
    const cardBgColor = accentRgb
      ? new Color(accentRgb.r, accentRgb.g, accentRgb.b, 0.1)
      : new Color(59, 130, 246, 0.1);

    // Text style from theme
    const bodyStyle = getBlockStyle(this.context.theme, 'body');
    const textColorHex = bodyStyle.textColor || '#000000';
    const textColor = this.parseColor(textColorHex);
    const fontName = bodyStyle.fontName || 'HelveticaNeue';

    // Calculate spacing
    const totalWidth = frame.width;
    const numEntries = entries.length;
    const spacing = totalWidth / (numEntries + 1);

    // Create the horizontal timeline line
    const [lineShapeType, linePointsArray] = ShapeFlowObject.derivePointsArray('rectangle');
    const timelineLine = new ShapeFlowObject(
      guidGenerator(),
      Color.iosToCssColor('00000000'),
      0,
      null,
      getCurrentDate(),
      {
        x: frame.x,
        y: LINE_Y - LINE_HEIGHT / 2,
        width: frame.width,
        height: LINE_HEIGHT,
      },
      null,
      1,
      null,
      null,
      'shape',
      null,
      null,
      0,
      { height: 0, width: 0 },
      null,
      null,
      getCurrentDate(),
      false,
      lineColor,
      lineShapeType,
      linePointsArray
    );
    timelineLine.cornerRadius = LINE_HEIGHT / 2;
    mmos.push(timelineLine);

    // Create markers and cards for each entry
    entries.forEach((entry, index) => {
      const x = frame.x + spacing * (index + 1);
      const isAbove = index % 2 === 0; // Alternate above/below

      // Create circular marker
      const [markerShapeType, markerPointsArray] = ShapeFlowObject.derivePointsArray('ellipse');
      const marker = new ShapeFlowObject(
        guidGenerator(),
        Color.iosToCssColor('00000000'),
        0,
        null,
        getCurrentDate(),
        {
          x: x - MARKER_SIZE / 2,
          y: LINE_Y - MARKER_SIZE / 2,
          width: MARKER_SIZE,
          height: MARKER_SIZE,
        },
        null,
        1,
        null,
        null,
        'shape',
        null,
        null,
        0,
        { height: 0, width: 0 },
        null,
        null,
        getCurrentDate(),
        false,
        markerColor,
        markerShapeType,
        markerPointsArray
      );
      mmos.push(marker);

      // Card position (above or below the line)
      const cardY = isAbove
        ? LINE_Y - MARKER_SIZE / 2 - CARD_GAP - CARD_HEIGHT
        : LINE_Y + MARKER_SIZE / 2 + CARD_GAP;

      // Create card background
      const [cardShapeType, cardPointsArray] = ShapeFlowObject.derivePointsArray('rectangle');
      const cardBg = new ShapeFlowObject(
        guidGenerator(),
        Color.iosToCssColor('00000000'),
        0,
        null,
        getCurrentDate(),
        {
          x: x - CARD_WIDTH / 2,
          y: cardY,
          width: CARD_WIDTH,
          height: CARD_HEIGHT,
        },
        null,
        1,
        null,
        null,
        'shape',
        null,
        null,
        0,
        { height: 0, width: 0 },
        null,
        null,
        getCurrentDate(),
        false,
        cardBgColor,
        cardShapeType,
        cardPointsArray
      );
      cardBg.cornerRadius = 8;
      mmos.push(cardBg);

      // Create label text (date/phase)
      const labelY = cardY + 8;
      const labelText = entry.label;
      const labelLength = labelText.length;
      const labelFontArray = `Alignment:${Alignment.CENTER}|Color:${lineColor.asIOS()}|Name:${fontName}-Bold|Size:${LABEL_FONT_SIZE.toFixed(6)}|Range:{0, ${labelLength}}`;
      const labelFontArraySimple = `Name:${fontName}-Bold|Size:${LABEL_FONT_SIZE.toFixed(6)}|Range:{0, ${labelLength}}`;

      const labelObj = new TextFlowObject(
        guidGenerator(),
        Color.default(),
        0,
        null,
        getCurrentDate(),
        {
          x: x - CARD_WIDTH / 2 + 8,
          y: labelY,
          width: CARD_WIDTH - 16,
          height: LABEL_HEIGHT,
        },
        null,
        1,
        null,
        null,
        'text',
        null,
        null,
        0,
        { height: 0, width: 0 },
        null,
        null,
        getCurrentDate(),
        false,
        false,
        lineColor,
        [FontAttrsArray.fromString(labelFontArraySimple)],
        null,
        Alignment.CENTER,
        {
          fontSize: LABEL_FONT_SIZE,
          fontName: `${fontName}-Bold`,
        },
        null,
        labelText,
        [FontAttrsArray.fromString(labelFontArray)]
      );
      mmos.push(labelObj);

      // Create title text
      const titleY = labelY + LABEL_HEIGHT + 4;
      const titleText = entry.title;
      const titleLength = titleText.length;
      const titleFontArray = `Alignment:${Alignment.CENTER}|Color:${textColor.asIOS()}|Name:${fontName}-Medium|Size:${TITLE_FONT_SIZE.toFixed(6)}|Range:{0, ${titleLength}}`;
      const titleFontArraySimple = `Name:${fontName}-Medium|Size:${TITLE_FONT_SIZE.toFixed(6)}|Range:{0, ${titleLength}}`;

      const titleObj = new TextFlowObject(
        guidGenerator(),
        Color.default(),
        0,
        null,
        getCurrentDate(),
        {
          x: x - CARD_WIDTH / 2 + 8,
          y: titleY,
          width: CARD_WIDTH - 16,
          height: 20,
        },
        null,
        1,
        null,
        null,
        'text',
        null,
        null,
        0,
        { height: 0, width: 0 },
        null,
        null,
        getCurrentDate(),
        false,
        false,
        textColor,
        [FontAttrsArray.fromString(titleFontArraySimple)],
        null,
        Alignment.CENTER,
        {
          fontSize: TITLE_FONT_SIZE,
          fontName: `${fontName}-Medium`,
        },
        null,
        titleText,
        [FontAttrsArray.fromString(titleFontArray)]
      );
      mmos.push(titleObj);

      // Create description text if present
      if (entry.description) {
        const descY = titleY + 22;
        const descText = entry.description;
        const descLength = descText.length;
        const descColor = this.parseColor(textColorHex);
        descColor.a = 0.7; // Slightly faded
        const descFontArray = `Alignment:${Alignment.CENTER}|Color:${descColor.asIOS()}|Name:${fontName}|Size:${DESC_FONT_SIZE.toFixed(6)}|Range:{0, ${descLength}}`;
        const descFontArraySimple = `Name:${fontName}|Size:${DESC_FONT_SIZE.toFixed(6)}|Range:{0, ${descLength}}`;

        const descObj = new TextFlowObject(
          guidGenerator(),
          Color.default(),
          0,
          null,
          getCurrentDate(),
          {
            x: x - CARD_WIDTH / 2 + 8,
            y: descY,
            width: CARD_WIDTH - 16,
            height: 28,
          },
          null,
          1,
          null,
          null,
          'text',
          null,
          null,
          0,
          { height: 0, width: 0 },
          null,
          null,
          getCurrentDate(),
          false,
          false,
          descColor,
          [FontAttrsArray.fromString(descFontArraySimple)],
          null,
          Alignment.CENTER,
          {
            fontSize: DESC_FONT_SIZE,
            fontName,
          },
          null,
          descText,
          [FontAttrsArray.fromString(descFontArray)]
        );
        mmos.push(descObj);
      }
    });

    return { mmos };
  }

  /**
   * Convert a stat block to MMOs (big number + label + context)
   */
  private statToMMOs(
    block: StatBlock,
    slot: LayoutSlot
  ): { mmos: (TextFlowObject | ShapeFlowObject)[] } {
    const mmos: (TextFlowObject | ShapeFlowObject)[] = [];
    const frame = frameToPixels(slot.frame, this.context.viewSize);

    // Theme colors
    const accentHex = this.context.theme.colors.accent?.[0] ||
      this.context.theme.colors.primary ||
      '#3B82F6';
    const bodyStyle = getBlockStyle(this.context.theme, 'body');
    const textColorHex = bodyStyle.textColor || '#000000';
    const fontName = bodyStyle.fontName || 'HelveticaNeue';

    // Layout: value (big), label (medium), context (small)
    const VALUE_SIZE = 72;
    const LABEL_SIZE = 20;
    const CONTEXT_SIZE = 14;
    const GAP = 8;

    // Value text (big number)
    const valueY = frame.y + frame.height * 0.2;
    const valueText = block.value;
    const valueColor = this.parseColor(accentHex);
    const valueLength = valueText.length;
    const valueFontArray = `Alignment:${Alignment.CENTER}|Color:${valueColor.asIOS()}|Name:${fontName}-Bold|Size:${VALUE_SIZE.toFixed(6)}|Range:{0, ${valueLength}}`;
    const valueFontArraySimple = `Name:${fontName}-Bold|Size:${VALUE_SIZE.toFixed(6)}|Range:{0, ${valueLength}}`;

    const valueObj = new TextFlowObject(
      guidGenerator(),
      Color.default(),
      0,
      null,
      getCurrentDate(),
      {
        x: frame.x,
        y: valueY,
        width: frame.width,
        height: VALUE_SIZE + 10,
      },
      null,
      1,
      null,
      null,
      'text',
      null,
      null,
      0,
      { height: 0, width: 0 },
      null,
      null,
      getCurrentDate(),
      false,
      false,
      valueColor,
      [FontAttrsArray.fromString(valueFontArraySimple)],
      null,
      Alignment.CENTER,
      {
        fontSize: VALUE_SIZE,
        fontName: `${fontName}-Bold`,
      },
      null,
      valueText,
      [FontAttrsArray.fromString(valueFontArray)]
    );
    mmos.push(valueObj);

    // Label text
    const labelY = valueY + VALUE_SIZE + GAP + 10;
    const labelText = block.label;
    const labelColor = this.parseColor(textColorHex);
    const labelLength = labelText.length;
    const labelFontArray = `Alignment:${Alignment.CENTER}|Color:${labelColor.asIOS()}|Name:${fontName}-Medium|Size:${LABEL_SIZE.toFixed(6)}|Range:{0, ${labelLength}}`;
    const labelFontArraySimple = `Name:${fontName}-Medium|Size:${LABEL_SIZE.toFixed(6)}|Range:{0, ${labelLength}}`;

    const labelObj = new TextFlowObject(
      guidGenerator(),
      Color.default(),
      0,
      null,
      getCurrentDate(),
      {
        x: frame.x,
        y: labelY,
        width: frame.width,
        height: LABEL_SIZE + 10,
      },
      null,
      1,
      null,
      null,
      'text',
      null,
      null,
      0,
      { height: 0, width: 0 },
      null,
      null,
      getCurrentDate(),
      false,
      false,
      labelColor,
      [FontAttrsArray.fromString(labelFontArraySimple)],
      null,
      Alignment.CENTER,
      {
        fontSize: LABEL_SIZE,
        fontName: `${fontName}-Medium`,
      },
      null,
      labelText,
      [FontAttrsArray.fromString(labelFontArray)]
    );
    mmos.push(labelObj);

    // Context text (if present)
    if (block.context) {
      const contextY = labelY + LABEL_SIZE + GAP + 10;
      const contextText = block.context;
      const contextColor = this.parseColor(textColorHex);
      contextColor.a = 0.6;
      const contextLength = contextText.length;
      const contextFontArray = `Alignment:${Alignment.CENTER}|Color:${contextColor.asIOS()}|Name:${fontName}|Size:${CONTEXT_SIZE.toFixed(6)}|Range:{0, ${contextLength}}`;
      const contextFontArraySimple = `Name:${fontName}|Size:${CONTEXT_SIZE.toFixed(6)}|Range:{0, ${contextLength}}`;

      const contextObj = new TextFlowObject(
        guidGenerator(),
        Color.default(),
        0,
        null,
        getCurrentDate(),
        {
          x: frame.x,
          y: contextY,
          width: frame.width,
          height: CONTEXT_SIZE + 10,
        },
        null,
        1,
        null,
        null,
        'text',
        null,
        null,
        0,
        { height: 0, width: 0 },
        null,
        null,
        getCurrentDate(),
        false,
        false,
        contextColor,
        [FontAttrsArray.fromString(contextFontArraySimple)],
        null,
        Alignment.CENTER,
        {
          fontSize: CONTEXT_SIZE,
          fontName,
        },
        null,
        contextText,
        [FontAttrsArray.fromString(contextFontArray)]
      );
      mmos.push(contextObj);
    }

    return { mmos };
  }

  /**
   * Convert a chart block to MMOs (SVG-like rendering with shapes and text)
   */
  private chartToMMOs(
    block: ChartBlock,
    slot: LayoutSlot
  ): { mmos: (TextFlowObject | ShapeFlowObject)[] } {
    const mmos: (TextFlowObject | ShapeFlowObject)[] = [];
    const frame = frameToPixels(slot.frame, this.context.viewSize);
    const data = block.data;

    if (!data || data.length === 0) {
      return { mmos };
    }

    // Theme colors
    const themeColors = [
      this.context.theme.colors.primary,
      ...(this.context.theme.colors.accent || []),
      this.context.theme.colors.secondary,
    ].filter(Boolean);
    const defaultColors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];
    const colorPalette = themeColors.length >= 3 ? themeColors : defaultColors;

    const bodyStyle = getBlockStyle(this.context.theme, 'body');
    const textColorHex = bodyStyle.textColor || '#000000';
    const fontName = bodyStyle.fontName || 'HelveticaNeue';

    // Title (if present)
    let contentY = frame.y;
    if (block.title) {
      const TITLE_SIZE = 18;
      const titleText = block.title;
      const titleColor = this.parseColor(textColorHex);
      const titleLength = titleText.length;
      const titleFontArray = `Alignment:${Alignment.CENTER}|Color:${titleColor.asIOS()}|Name:${fontName}-Medium|Size:${TITLE_SIZE.toFixed(6)}|Range:{0, ${titleLength}}`;
      const titleFontArraySimple = `Name:${fontName}-Medium|Size:${TITLE_SIZE.toFixed(6)}|Range:{0, ${titleLength}}`;

      const titleObj = new TextFlowObject(
        guidGenerator(),
        Color.default(),
        0,
        null,
        getCurrentDate(),
        {
          x: frame.x,
          y: contentY,
          width: frame.width,
          height: TITLE_SIZE + 10,
        },
        null,
        1,
        null,
        null,
        'text',
        null,
        null,
        0,
        { height: 0, width: 0 },
        null,
        null,
        getCurrentDate(),
        false,
        false,
        titleColor,
        [FontAttrsArray.fromString(titleFontArraySimple)],
        null,
        Alignment.CENTER,
        {
          fontSize: TITLE_SIZE,
          fontName: `${fontName}-Medium`,
        },
        null,
        titleText,
        [FontAttrsArray.fromString(titleFontArray)]
      );
      mmos.push(titleObj);
      contentY += TITLE_SIZE + 20;
    }

    const chartHeight = frame.height - (contentY - frame.y) - 20;
    const chartWidth = frame.width;

    if (block.chartType === 'bar') {
      // Horizontal bar chart
      const maxValue = Math.max(...data.map(d => d.value));
      const BAR_HEIGHT = 24;
      const BAR_GAP = 12;
      const LABEL_WIDTH = 80;
      const VALUE_WIDTH = 50;

      data.forEach((item, index) => {
        const y = contentY + index * (BAR_HEIGHT + BAR_GAP);
        const barWidth = (item.value / maxValue) * (chartWidth - LABEL_WIDTH - VALUE_WIDTH - 20);
        const colorHex = item.color || colorPalette[index % colorPalette.length];
        const barColorRgb = hexToRgb(colorHex);
        const barColor = barColorRgb
          ? new Color(barColorRgb.r, barColorRgb.g, barColorRgb.b, 1)
          : new Color(59, 130, 246, 1);

        // Label
        const labelText = item.label;
        const labelColor = this.parseColor(textColorHex);
        const labelLength = labelText.length;
        const LABEL_SIZE = 12;
        const labelFontArray = `Alignment:${Alignment.RIGHT}|Color:${labelColor.asIOS()}|Name:${fontName}|Size:${LABEL_SIZE.toFixed(6)}|Range:{0, ${labelLength}}`;
        const labelFontArraySimple = `Name:${fontName}|Size:${LABEL_SIZE.toFixed(6)}|Range:{0, ${labelLength}}`;

        const labelObj = new TextFlowObject(
          guidGenerator(),
          Color.default(),
          0,
          null,
          getCurrentDate(),
          {
            x: frame.x,
            y: y + (BAR_HEIGHT - LABEL_SIZE) / 2,
            width: LABEL_WIDTH - 10,
            height: LABEL_SIZE + 4,
          },
          null,
          1,
          null,
          null,
          'text',
          null,
          null,
          0,
          { height: 0, width: 0 },
          null,
          null,
          getCurrentDate(),
          false,
          false,
          labelColor,
          [FontAttrsArray.fromString(labelFontArraySimple)],
          null,
          Alignment.RIGHT,
          {
            fontSize: LABEL_SIZE,
            fontName,
          },
          null,
          labelText,
          [FontAttrsArray.fromString(labelFontArray)]
        );
        mmos.push(labelObj);

        // Bar
        const [barShapeType, barPointsArray] = ShapeFlowObject.derivePointsArray('rectangle');
        const barShape = new ShapeFlowObject(
          guidGenerator(),
          Color.iosToCssColor('00000000'),
          0,
          null,
          getCurrentDate(),
          {
            x: frame.x + LABEL_WIDTH,
            y: y,
            width: Math.max(barWidth, 4),
            height: BAR_HEIGHT,
          },
          null,
          1,
          null,
          null,
          'shape',
          null,
          null,
          0,
          { height: 0, width: 0 },
          null,
          null,
          getCurrentDate(),
          false,
          barColor,
          barShapeType,
          barPointsArray
        );
        barShape.cornerRadius = 4;
        mmos.push(barShape);

        // Value
        const valueText = String(item.value);
        const valueColor = this.parseColor(textColorHex);
        const valueLength = valueText.length;
        const VALUE_SIZE = 12;
        const valueFontArray = `Alignment:${Alignment.LEFT}|Color:${valueColor.asIOS()}|Name:${fontName}-Medium|Size:${VALUE_SIZE.toFixed(6)}|Range:{0, ${valueLength}}`;
        const valueFontArraySimple = `Name:${fontName}-Medium|Size:${VALUE_SIZE.toFixed(6)}|Range:{0, ${valueLength}}`;

        const valueObj = new TextFlowObject(
          guidGenerator(),
          Color.default(),
          0,
          null,
          getCurrentDate(),
          {
            x: frame.x + LABEL_WIDTH + barWidth + 8,
            y: y + (BAR_HEIGHT - VALUE_SIZE) / 2,
            width: VALUE_WIDTH,
            height: VALUE_SIZE + 4,
          },
          null,
          1,
          null,
          null,
          'text',
          null,
          null,
          0,
          { height: 0, width: 0 },
          null,
          null,
          getCurrentDate(),
          false,
          false,
          valueColor,
          [FontAttrsArray.fromString(valueFontArraySimple)],
          null,
          Alignment.LEFT,
          {
            fontSize: VALUE_SIZE,
            fontName: `${fontName}-Medium`,
          },
          null,
          valueText,
          [FontAttrsArray.fromString(valueFontArray)]
        );
        mmos.push(valueObj);
      });
    } else if (block.chartType === 'pie' || block.chartType === 'donut') {
      // Pie/donut chart - use shapes to approximate segments
      const total = data.reduce((sum, d) => sum + d.value, 0);
      const centerX = frame.x + chartWidth / 2;
      const centerY = contentY + chartHeight / 2;
      const radius = Math.min(chartWidth, chartHeight) / 2 - 40;
      const innerRadius = block.chartType === 'donut' ? radius * 0.5 : 0;

      // Create a simple legend instead of actual pie segments (since we can't do SVG paths)
      const LEGEND_ITEM_HEIGHT = 24;
      const LEGEND_DOT_SIZE = 12;
      const legendStartY = contentY + 20;

      data.forEach((item, index) => {
        const y = legendStartY + index * LEGEND_ITEM_HEIGHT;
        const colorHex = item.color || colorPalette[index % colorPalette.length];
        const dotColorRgb = hexToRgb(colorHex);
        const dotColor = dotColorRgb
          ? new Color(dotColorRgb.r, dotColorRgb.g, dotColorRgb.b, 1)
          : new Color(59, 130, 246, 1);

        // Color dot
        const [dotShapeType, dotPointsArray] = ShapeFlowObject.derivePointsArray('ellipse');
        const dotShape = new ShapeFlowObject(
          guidGenerator(),
          Color.iosToCssColor('00000000'),
          0,
          null,
          getCurrentDate(),
          {
            x: frame.x + 20,
            y: y + (LEGEND_ITEM_HEIGHT - LEGEND_DOT_SIZE) / 2,
            width: LEGEND_DOT_SIZE,
            height: LEGEND_DOT_SIZE,
          },
          null,
          1,
          null,
          null,
          'shape',
          null,
          null,
          0,
          { height: 0, width: 0 },
          null,
          null,
          getCurrentDate(),
          false,
          dotColor,
          dotShapeType,
          dotPointsArray
        );
        mmos.push(dotShape);

        // Legend text with percentage
        const percentage = ((item.value / total) * 100).toFixed(1);
        const legendText = `${item.label}: ${percentage}%`;
        const legendColor = this.parseColor(textColorHex);
        const legendLength = legendText.length;
        const LEGEND_SIZE = 14;
        const legendFontArray = `Alignment:${Alignment.LEFT}|Color:${legendColor.asIOS()}|Name:${fontName}|Size:${LEGEND_SIZE.toFixed(6)}|Range:{0, ${legendLength}}`;
        const legendFontArraySimple = `Name:${fontName}|Size:${LEGEND_SIZE.toFixed(6)}|Range:{0, ${legendLength}}`;

        const legendObj = new TextFlowObject(
          guidGenerator(),
          Color.default(),
          0,
          null,
          getCurrentDate(),
          {
            x: frame.x + 20 + LEGEND_DOT_SIZE + 10,
            y: y + (LEGEND_ITEM_HEIGHT - LEGEND_SIZE) / 2,
            width: chartWidth - 60,
            height: LEGEND_SIZE + 4,
          },
          null,
          1,
          null,
          null,
          'text',
          null,
          null,
          0,
          { height: 0, width: 0 },
          null,
          null,
          getCurrentDate(),
          false,
          false,
          legendColor,
          [FontAttrsArray.fromString(legendFontArraySimple)],
          null,
          Alignment.LEFT,
          {
            fontSize: LEGEND_SIZE,
            fontName,
          },
          null,
          legendText,
          [FontAttrsArray.fromString(legendFontArray)]
        );
        mmos.push(legendObj);
      });
    }

    return { mmos };
  }

  /**
   * Create a TextFlowObject with given properties
   */
  private createTextFlowObject(
    text: string,
    frame: { x: number; y: number; width: number; height: number },
    style: {
      fontSize?: number;
      fontName?: string;
      textColor?: string;
      textAlignment?: 'left' | 'center' | 'right';
      lineHeight?: number;
    }
  ): TextFlowObject {
    const fontSize = style.fontSize || 24;
    const fontName = style.fontName || 'HelveticaNeue';
    const textColor = this.parseColor(style.textColor || '#000000');

    const alignment = this.parseAlignment(style.textAlignment || 'left');
    const textLength = text.length;

    // Create font arrays
    const fontArrayString = `Alignment:${alignment}|Color:${textColor.asIOS()}|Name:${fontName}|Size:${fontSize.toFixed(6)}|Range:{0, ${textLength}}`;
    const fontArrayStringSimple = `Name:${fontName}|Size:${fontSize.toFixed(6)}|Range:{0, ${textLength}}`;

    return new TextFlowObject(
      guidGenerator(),
      Color.default(),
      0,
      null,
      getCurrentDate(),
      {
        x: frame.x,
        y: frame.y,
        width: frame.width,
        height: frame.height,
      },
      null,
      1,
      null,
      null,
      'text',
      null,
      null,
      0,
      { height: 0, width: 0 },
      null,
      null,
      getCurrentDate(),
      false,
      false,
      textColor,
      [FontAttrsArray.fromString(fontArrayStringSimple)],
      null,
      alignment,
      {
        fontSize,
        fontName,
      },
      null,
      text,
      [FontAttrsArray.fromString(fontArrayString)]
    );
  }

  /**
   * Create a placeholder shape for media blocks
   */
  private createPlaceholderShape(
    block: ImagePlaceholderBlock,
    frame: { x: number; y: number; width: number; height: number }
  ): ShapeFlowObject {
    // Use the theme's accent/primary color at 15% opacity for placeholder
    const accentHex = this.context.theme.colors.accent?.[0] || this.context.theme.colors.primary || '#CCCCCC';
    const accentRgb = hexToRgb(accentHex);
    const placeholderColor = accentRgb
      ? new Color(accentRgb.r, accentRgb.g, accentRgb.b, 0.15)
      : Color.iosToCssColor('CCCCCC26');

    const shape = new ShapeFlowObject(
      guidGenerator(),
      Color.iosToCssColor('00000000'),
      0,
      null,
      getCurrentDate(),
      {
        x: frame.x,
        y: frame.y,
        width: frame.width,
        height: frame.height,
      },
      null,
      1,
      null,
      null,
      'shape',
      null,
      null,
      0,
      { height: 0, width: 0 },
      null,
      null,
      getCurrentDate(),
      false,
      placeholderColor,
      'rectangle' as any,
      ''
    );

    // Mark as image placeholder for UI rendering (pulsing animation + "Loading image" text)
    shape.__isImagePlaceholder = true;
    // Also set a serializable marker that survives JSON export
    (shape as any).aiImagePlaceholder = true;
    (shape as any).aiBlockId = block.id;
    (shape as any).aiImageDescription = block.description;

    return shape;
  }

  /**
   * Create an ImageFlowObject for a resolved image placeholder.
   * Preserves the image's aspect ratio within the slot frame.
   */
  private createImageMMO(
    block: ImagePlaceholderBlock,
    frame: { x: number; y: number; width: number; height: number },
    isBackgroundSlot: boolean = false
  ): ImageFlowObject {
    // For background/hero images, use full frame (cover behavior)
    // For regular images, fit within frame while preserving aspect ratio (contain behavior)
    const adjustedFrame = isBackgroundSlot
      ? frame
      : this.fitImageToFrame(frame, block.aspectRatio || '16:9');
    const img = ImageFlowObject.default(
      { __tag: 'string', value: block.resolvedImageUrl! },
      '',
      adjustedFrame.width,
      adjustedFrame.height
    );
    img.frame = adjustedFrame;
    return img;
  }

  /**
   * Fit an image within a slot frame while preserving its aspect ratio.
   * Centers the image within the slot if there's leftover space.
   */
  private fitImageToFrame(
    frame: { x: number; y: number; width: number; height: number },
    aspectRatio: string
  ): { x: number; y: number; width: number; height: number } {
    const parts = aspectRatio.split(':').map(Number);
    if (parts.length !== 2 || parts[0] <= 0 || parts[1] <= 0) {
      return frame;
    }
    const imageRatio = parts[0] / parts[1];
    const slotRatio = frame.width / frame.height;

    let newWidth = frame.width;
    let newHeight = frame.height;

    if (imageRatio > slotRatio) {
      // Image is wider than slot — fit to width, shrink height
      newHeight = frame.width / imageRatio;
    } else {
      // Image is taller than slot — fit to height, shrink width
      newWidth = frame.height * imageRatio;
    }

    // Center horizontally, top-align vertically
    const x = frame.x + (frame.width - newWidth) / 2;
    const y = frame.y;

    return { x: Math.round(x), y: Math.round(y), width: Math.round(newWidth), height: Math.round(newHeight) };
  }

  /**
   * Generate decoration MMOs for a screen based on the theme's decoration definitions.
   * Returns MMOs ordered bottom-to-top for z-ordering.
   * Uses auto-conform system to adapt decorations to the current view size.
   */
  private generateDecorationMMOs(
    screenType: ScreenContent['screenType']
  ): (ShapeFlowObject | ImageFlowObject)[] {
    // Use the new conform system which handles both legacy and abstract decorations
    const decorationElements = getDecorationsForScreenWithConform(
      this.context.theme.decorations,
      screenType,
      this.context.viewSize
    );

    if (decorationElements.length === 0) {
      return [];
    }

    const mmos: (ShapeFlowObject | ImageFlowObject)[] = [];

    for (const element of decorationElements) {
      const mmo = this.decorationElementToMMO(element);
      if (mmo) {
        mmos.push(mmo);
      }
    }

    return mmos;
  }

  /**
   * Convert a single DecorationElement to an MMO
   */
  private decorationElementToMMO(
    element: DecorationElement
  ): ShapeFlowObject | ImageFlowObject | null {
    const frame = element.frame;

    switch (element.type) {
      case 'image': {
        if (!element.imageUrl) return null;
        // Convert relative theme image paths to absolute URLs so Treemo can access them
        let imageUrl = element.imageUrl;
        if (imageUrl.startsWith('/include/img/')) {
          imageUrl = `${window.location.origin}${imageUrl}`;
        }
        const img = ImageFlowObject.default(
          { __tag: 'string', value: imageUrl },
          '',
          frame.width,
          frame.height
        );
        img.frame = { x: frame.x, y: frame.y, width: frame.width, height: frame.height };
        img.locked = false;
        return img;
      }

      case 'shape': {
        const { hex6, alpha } = parseDecorationColor(element.color || '#000000');
        const color = Color.fromHex(hex6);
        color.a = alpha;

        const [shapeType, pointsArrayString] = ShapeFlowObject.derivePointsArray(
          element.shapeType || 'rectangle'
        );

        const shape = new ShapeFlowObject(
          guidGenerator(),
          Color.iosToCssColor('00000000'),
          element.rotation || 0,
          null,
          getCurrentDate(),
          { x: frame.x, y: frame.y, width: frame.width, height: frame.height },
          null,
          1,
          null,
          null,
          'shape',
          null,
          null,
          0,
          { height: 0, width: 0 },
          null,
          null,
          getCurrentDate(),
          false,
          color,
          shapeType,
          pointsArrayString
        );
        return shape;
      }

      case 'gradient-shape': {
        if (!element.gradient) return null;

        // Use transparent as base color — the gradient renders via SVG defs
        const baseColor = new Color(0, 0, 0, 0);

        const [shapeType, pointsArrayString] = ShapeFlowObject.derivePointsArray(
          element.shapeType || 'rectangle'
        );

        const shape = new ShapeFlowObject(
          guidGenerator(),
          Color.iosToCssColor('00000000'),
          element.rotation || 0,
          null,
          getCurrentDate(),
          { x: frame.x, y: frame.y, width: frame.width, height: frame.height },
          null,
          1,
          null,
          null,
          'shape',
          null,
          null,
          0,
          { height: 0, width: 0 },
          null,
          null,
          getCurrentDate(),
          false,
          baseColor,
          shapeType,
          pointsArrayString
        );
        shape.gradientDef = element.gradient;
        return shape;
      }

      default:
        return null;
    }
  }

  /**
   * Create a Screen object
   */
  private createScreen(
    screenContent: ScreenContent,
    mmos: (TextFlowObject | ShapeFlowObject | ImageFlowObject)[],
    screenIndex: number
  ): Screens {
    const backgroundColor = this.getBackgroundColor(screenContent.backgroundHint);
    // Use pre-generated ID for TOC navigation to work correctly
    const screenId = this.screenIdMap.get(screenIndex) || guidGenerator();

    return new Screens(
      backgroundColor,
      getCurrentDate(),
      screenId,
      mmos,
      ImageAsset.default({ __tag: 'string', value: '' }),
      'no',
      ImageAsset.default({ __tag: 'string', value: '' }),
      '',
      false,
      '',
      '',
      getCurrentDate(),
      ImageAsset.default({ __tag: 'string', value: '' })
    );
  }

  /**
   * Create the final Flow object
   */
  private createFlow(
    screenResults: (LayoutCompilationResult & { screen: Screens })[]
  ): Flow {
    const screens = screenResults.map((r) => r.screen);

    // Debug: Warn about empty screens (blank screens with no content)
    screens.forEach((screen, index) => {
      if (screen.mixedMediaObjects.length === 0) {
        console.warn(`[FlowCompiler] Screen ${index} has 0 MMOs (will appear blank)`);
        this.warnings.push(`Screen ${index + 1} has no content objects (appears blank)`);
      }
    });

    return new Flow(
      'NO',
      getCurrentDate(),
      guidGenerator(),
      screens,
      this.context.theme.flowvellaThemeId || '10',
      this.context.presentation.title,
      getCurrentDate(),
      this.context.viewSize
    );
  }

  /**
   * Get background color based on hint
   */
  private getBackgroundColor(hint?: 'light' | 'dark' | 'image' | 'gradient'): Color {
    const bgColor = this.context.theme.colors.background;
    const rgb = hexToRgb(bgColor);

    if (!rgb) {
      return new Color(255, 255, 255, 1);
    }

    switch (hint) {
      case 'dark':
        // Darken the background
        return new Color(
          Math.max(0, rgb.r - 30),
          Math.max(0, rgb.g - 30),
          Math.max(0, rgb.b - 30),
          1
        );
      case 'light':
      default:
        return new Color(rgb.r, rgb.g, rgb.b, 1);
    }
  }

  /**
   * Parse a color string to Color object
   */
  private parseColor(color: string): Color {
    if (color.startsWith('#')) {
      return Color.fromHex(color);
    }
    return Color.iosToCssColor(color);
  }

  /**
   * Parse alignment string to Alignment enum
   */
  private parseAlignment(alignment: 'left' | 'center' | 'right'): Alignment {
    switch (alignment) {
      case 'center':
        return Alignment.CENTER;
      case 'right':
        return Alignment.RIGHT;
      case 'left':
      default:
        return Alignment.LEFT;
    }
  }

  /**
   * Create an empty presentation
   */
  private createEmptyPresentation(): PresentationContent {
    return {
      id: guidGenerator(),
      title: 'Untitled Presentation',
      screens: [],
      globalMetadata: {
        topic: '',
        tone: 'professional',
      },
      schemaVersion: '1.0.0',
    };
  }

  /**
   * Update the theme
   */
  setTheme(theme: StyleTheme): void {
    this.context.theme = theme;
  }

  /**
   * Update the layouts
   */
  setLayouts(layouts: Map<string, LayoutTemplate>): void {
    this.context.layouts = layouts;
  }

  /**
   * Update the view size
   */
  setViewSize(viewSize: { width: number; height: number }): void {
    this.context.viewSize = viewSize;
  }
}

/**
 * Factory function to create a compiler with defaults
 */
export function createFlowCompiler(
  options: Partial<CompilationContext> = {}
): FlowCompiler {
  return new FlowCompiler(options);
}

/**
 * Quick compile function for simple use cases
 */
export function compilePresentation(
  presentation: PresentationContent,
  theme?: StyleTheme,
  overrideLayoutIds?: string[]
): FlowCompilationResult {
  const compiler = new FlowCompiler({
    presentation,
    theme: theme || DEFAULT_THEME,
    layouts: PREDEFINED_LAYOUTS,
    viewSize: DEFAULT_VIEW_SIZE,
    overrideLayoutIds,
  });

  return compiler.compile();
}
